import type { FC, ReactNode } from 'react'
import type { NumberFormat } from '@/lib/format'

import { ArrowRight, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCompactCurrency, formatValue } from '@/lib/format'
import { BarChart } from '@/components/charts/bar-chart'
import { DonutChart } from '@/components/charts/donut-chart'
import { MiniBars } from '@/components/charts/mini-bars'
import { Sparkline } from '@/components/charts/sparkline'
import { DeltaBadge } from '@/components/charts/delta-badge'
import { ProgressBar } from '@/components/charts/progress-bar'

import {
    CHART_COLORS,
    EXPENSE_CATEGORIES,
    EXPENSE_CHANGE,
    EXPENSE_SERIES,
    INCOME_CHANGE,
    INCOME_SERIES,
    MONTHS,
    NET_CASH_FLOW,
    TOTAL_EXPENSES,
    TOTAL_INCOME
} from '@/constants/dashboard'

const CARD = 'rounded-2xl border border-burgundy/10 bg-ivory/95 p-4 text-left text-burgundy shadow-[0_30px_70px_-30px_rgb(57_26_24/0.5)] backdrop-blur'

interface CountProps {
    value: number
    format: NumberFormat
    decimals?: number
}

/** Number the scroll timeline counts up. Renders its final value on the server. */
const Count: FC<CountProps> = ({ value, format, decimals }): ReactNode => {
    return (
        <span data-count={value} data-format={format} data-decimals={decimals} className='tabular-nums'>
            {formatValue(value, format, decimals)}
        </span>
    )
}

const CardHeader: FC<{ title: string, meta: string }> = ({ title, meta }): ReactNode => {
    return (
        <div className='flex items-center justify-between gap-3'>
            <p className='text-xs font-medium'>{title}</p>
            <span className='font-mono text-[9px] uppercase tracking-[0.14em] text-stone'>{meta}</span>
        </div>
    )
}

export const StoryRevenueCard: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <div className={cn(CARD, className)}>
            <CardHeader title='Revenue' meta='12 months' />

            <p className='mt-2 flex items-baseline gap-2 font-heading text-2xl font-medium tracking-[-0.03em]'>
                <Count value={4167420} format='compact' />
                <span className='font-sans text-[11px] font-normal tracking-normal text-stone'>annual run rate</span>
            </p>

            <BarChart
                title='Monthly revenue for the last 12 months'
                labels={MONTHS}
                series={[{ name: 'Revenue', values: INCOME_SERIES, color: CHART_COLORS.burgundy }]}
                height={78}
                gridLines={2}
                showLabels={false}
                interactive={false}
                animation='manual'
                highlightIndex={11}
                formatValue={formatCompactCurrency}
                className='mt-3'
            />
        </div>
    )
}

export const StoryCategoriesCard: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <div className={cn(CARD, className)}>
            <CardHeader title='Spend by category' meta='September' />

            <div className='mt-3 flex items-center gap-4'>
                <DonutChart
                    title='Spend by category'
                    segments={EXPENSE_CATEGORIES}
                    size={96}
                    thickness={10}
                    legend='none'
                    interactive={false}
                    animation='manual'
                    centerLabel='Total'
                    formatValue={formatCompactCurrency}
                />

                <ul className='min-w-0 flex-1 space-y-1.5'>
                    {EXPENSE_CATEGORIES.slice(0, 4).map(category => (
                        <li key={category.name} className='flex items-center gap-2 text-[11px]'>
                            <span aria-hidden='true' className='size-2 shrink-0 rounded-[2px]' style={{ backgroundColor: category.color }} />
                            <span className='truncate text-stone-dark'>{category.name}</span>
                            <span className='ml-auto tabular-nums text-stone'>{Math.round((category.value / TOTAL_EXPENSES) * 100)}%</span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
}

export const StoryIncomeCard: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <div className={cn(CARD, className)}>
            <CardHeader title='Total income' meta='Sep 2026' />

            <p className='mt-2 font-heading text-[1.65rem] font-medium leading-none tracking-[-0.03em]'>
                <Count value={TOTAL_INCOME} format='currency' />
            </p>

            <div className='mt-2'>
                <DeltaBadge value={INCOME_CHANGE} suffix='vs Aug' />
            </div>

            <Sparkline values={INCOME_SERIES} color={CHART_COLORS.gold} height={42} animation='manual' className='mt-3' />
        </div>
    )
}

export const StoryExpensesCard: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <div className={cn(CARD, className)}>
            <CardHeader title='Total expenses' meta='Sep 2026' />

            <p className='mt-2 font-heading text-[1.65rem] font-medium leading-none tracking-[-0.03em]'>
                <Count value={TOTAL_EXPENSES} format='currency' />
            </p>

            <div className='mt-2'>
                <DeltaBadge value={EXPENSE_CHANGE} good suffix='vs Aug' />
            </div>

            <MiniBars values={EXPENSE_SERIES.slice(-8)} color={CHART_COLORS.burgundy} height={42} animation='manual' className='mt-3' />
        </div>
    )
}

export const StoryCashFlowCard: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <div className={cn(CARD, className)}>
            <CardHeader title='Net cash flow' meta='This month' />

            <div className='mt-2 flex items-center gap-2'>
                <p className='font-heading text-xl font-medium tracking-[-0.02em] text-positive'>
                    +<Count value={NET_CASH_FLOW} format='currency' />
                </p>

                <span className='rounded-full bg-sage/25 px-2 py-0.5 text-[10px] font-medium text-sage-dark'>Healthy</span>
            </div>

            <div className='mt-3 space-y-2 text-[11px]'>
                <div>
                    <div className='mb-1 flex justify-between text-stone-dark'><span>Money in</span><span className='tabular-nums'>$482K</span></div>
                    <ProgressBar value={1} color={CHART_COLORS.gold} animation='manual' />
                </div>

                <div>
                    <div className='mb-1 flex justify-between text-stone-dark'><span>Money out</span><span className='tabular-nums'>$317K</span></div>
                    <ProgressBar value={TOTAL_EXPENSES / TOTAL_INCOME} color={CHART_COLORS.burgundy} animation='manual' />
                </div>
            </div>
        </div>
    )
}

export const StoryPerformanceCard: FC<{ className?: string }> = ({ className }): ReactNode => {
    const metrics = [
        { label: 'Gross margin', value: 72.4, progress: 0.724 },
        { label: 'Operating margin', value: 34.3, progress: 0.686 },
        { label: 'Budget used', value: 81.6, progress: 0.816 }
    ]

    return (
        <div className={cn(CARD, className)}>
            <CardHeader title='Performance' meta='Q3 2026' />

            <ul className='mt-3 space-y-2.5'>
                {metrics.map(metric => (
                    <li key={metric.label} className='text-[11px]'>
                        <div className='mb-1 flex justify-between'>
                            <span className='text-stone-dark'>{metric.label}</span>
                            <span className='font-medium'><Count value={metric.value} format='percent' decimals={1} /></span>
                        </div>

                        <ProgressBar value={metric.progress} color={CHART_COLORS.sage} animation='manual' />
                    </li>
                ))}
            </ul>
        </div>
    )
}

export const StoryInsightCard: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <div className={cn(CARD, 'bg-burgundy text-cream', className)}>
            <p className='flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-beige'>
                <span className='flex size-6 items-center justify-center rounded-full bg-cream/10 text-gold'>
                    <Sparkles aria-hidden='true' className='size-3.5' />
                </span>
                AI insight
            </p>

            <p className='mt-3 text-[13px] leading-relaxed text-cream/90'>
                Software spend is up 18% this quarter. Consolidating three overlapping tools could save about $4,200 a month.
            </p>

            <p className='mt-3 inline-flex items-center gap-1 text-[11px] font-medium text-gold'>
                Review tools
                <ArrowRight aria-hidden='true' className='size-3' />
            </p>
        </div>
    )
}
